import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import { useTransactionsStore } from '~/stores/transactions-store';
import {
  TransactionStatusCode,
  TransactionMultiStepWorkflowstatus
} from '~/enums/transactions/transaction-status.enum';
import type { TransactionHistoryStep } from '~/types';
import useTransactionsApi from '../api/useTransactionsApi';
import { useCheckRequest } from './useCheckRequest';

export const useTransactionHistory = () => {
  const { getTransactionHistory } = useTransactionsApi();
  const store = useTransactionsStore();
  const { getMoneyOutValidationConfig } = useCheckRequest();
  const { t, locale } = useI18n();
  const route = useRoute();
  const id = route.params.id as string;

  const isLoading = ref(false);

  const formatValidationStep = (statusKey: string, validation: number, totalValidator: number) => {
    switch (statusKey) {
      case TransactionStatusCode.Requested:
        return {
          title: t('transactions.details.transactionHistory.steps.initialized'),
          status: 'progress',
          chipItem: {
            text: t('transactions.status.requested'),
            theme: 'warning'
          }
        };
      case TransactionStatusCode.Completed:
        return {
          title: t('transactions.details.transactionHistory.steps.finalized'),
          status: 'done',
          chipItem: {
            text: t('transactions.status.completed'),
            theme: 'success'
          }
        };
      case TransactionStatusCode.Cancelled:
        return {
          title: t('transactions.details.transactionHistory.steps.finalized'),
          status: 'danger-error',
          chipItem: {
            text: t('transactions.status.cancelled'),
            theme: 'danger'
          }
        };
      case TransactionMultiStepWorkflowstatus.Approved:
        return {
          title: t('transactions.details.transactionHistory.steps.approved', {
            validationCounter: validation + '/' + totalValidator
          }),
          status: 'done'
        };
      case TransactionMultiStepWorkflowstatus.Refused:
        return {
          title: t('transactions.details.transactionHistory.steps.refused'),
          status: 'danger-error'
        };
      default:
        return {};
    }
  };

  const timelineItems = computed(() => {
    const transactionStatus = store.transactionInfos?.status as TransactionStatusCode;
    const moneyOutValidationConfig = getMoneyOutValidationConfig();
    const numberOfValidators =
      transactionStatus === TransactionStatusCode.Completed
        ? store.history.filter((step) => step.ActionResult).length
        : moneyOutValidationConfig.moneyOut.numberOfValidators;
    const numberOfValidatorsNumber = parseInt(numberOfValidators);

    let formattedTimelineItems: any[] = [];
    let validationCount = 0;
    formattedTimelineItems = store.history.map((step) => {
      const status = step.Status ? step.Status : step.ActionResult;
      if (status) {
        if (status === TransactionMultiStepWorkflowstatus.Approved) {
          validationCount++;
        }
        const validationStep = formatValidationStep(
          status,
          validationCount,
          numberOfValidatorsNumber
        );
        return {
          title: validationStep.title,
          description: `${t('common.by')} ${step.User}`,
          startDate: `${t('common.date')}: ${getFormattedDate(step.Date, 'L LTS', locale.value)}`,
          status: validationStep.status,
          chipItem: validationStep.chipItem,
          comment: step.Comment
        };
      } else return null;
    });

    if (
      validationCount < numberOfValidatorsNumber &&
      transactionStatus === TransactionStatusCode.Requested &&
      store.history[store.history.length - 1]?.Status !== TransactionStatusCode.Cancelled &&
      store.history[store.history.length - 1]?.ActionResult !==
        TransactionMultiStepWorkflowstatus.Refused
    ) {
      formattedTimelineItems.push({
        title: t('transactions.details.transactionHistory.steps.waiting', {
          validationCounter:
            numberOfValidatorsNumber > 1 ? validationCount + 1 + '/' + numberOfValidatorsNumber : ''
        })
      });
    }
    return formattedTimelineItems;
  });

  const loadTransactionHistory = async () => {
    const data = await getTransactionHistory(id);
    store.history = data as TransactionHistoryStep[];
  };

  return {
    timelineItems,
    isLoading,
    loadTransactionHistory
  };
};
